import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

const currencies = ["EUR", "USD", "GBP", "CHF", "AUD", "AED", "INR"];

export function Rates({ hourlyRate, hourlyCurrency, dailyRate, dailyCurrency, saveRates }) {
  const [isEditing, setIsEditing] = useState(false);

  const [hourRate, setHourRate] = useState("");
  const [hourCurrency, setHourCurrency] = useState("");
  const [dayRate, setDayRate] = useState("");
  const [dayCurrency, setDayCurrency] = useState("");

  useEffect(() => {
    setHourRate(hourlyRate);
    setHourCurrency(hourlyCurrency);
    setDayRate(dailyRate);
    setDayCurrency(dailyCurrency);
  }, [hourlyRate, hourlyCurrency, dailyRate, dailyCurrency]);

  const handleSave = () => {
    saveRates({
      hourlyRate: hourRate,
      hourlyCurrency: hourCurrency,
      dailyRate: dayRate,
      dailyCurrency: dayCurrency,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setHourRate(hourlyRate);
    setHourCurrency(hourlyCurrency);
    setDayRate(dailyRate);
    setDayCurrency(dailyCurrency);
    setIsEditing(false);
  };

  return (
    <div className={`bg-white rounded-xl p-6 relative transition-all duration-300 ${isEditing ? "glow-burst glow-hold" : "shadow"}`}>
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-semibold mb-2">{!isEditing ? 'Rates' : 'Edit Rates'}</h3>

        {!isEditing ? (
          <button
            className="absolute top-5 right-6 text-gray-500 hover:text-black hover:bg-gray-200 focus:outline-none py-1 px-2 rounded-full transition"
            onClick={() => setIsEditing(true)}
            title="Edit Rates"
          >
            <i className="fa-solid fa-pencil" />
          </button>
        ) : (
          <div className="absolute top-5 right-6 flex gap-2">
            <button
              className="text-gray-500 hover:text-black focus:outline-none py-1 px-2.5 rounded-full hover:bg-gray-200 transition"
              onClick={handleSave}
              title="Save"
            >
              <i className="fa-solid fa-floppy-disk" />
            </button>
            <button
              className="text-gray-500 hover:text-black focus:outline-none py-1 px-2.5 rounded-full hover:bg-gray-200 transition"
              onClick={handleCancel}
              title="Cancel"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!isEditing ? (
          <motion.div
            key="rates-view"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden flex flex-col gap-1"
          >
            <p className="text-sm text-gray-700">
              {hourlyCurrency || hourlyRate ? (
                <>
                  {hourlyCurrency} {hourlyRate} <span>per hour</span>
                </>
              ) : (
                <span className="text-gray-500">No hourly rate mentioned yet!</span>
              )}
            </p>
            <p className="text-sm text-gray-700">
              {dailyCurrency || dailyRate ? (
                <>
                  {dailyCurrency} {dailyRate} <span>per day</span>
                </>
              ) : (
                <span className="text-gray-500">No daily rate mentioned yet!</span>
              )}
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="rates-edit"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden flex flex-col gap-2"
          >
            {/* Hourly */}
            <div className="flex items-center gap-2">
              <input
                list="currency-options"
                value={hourCurrency}
                onChange={(e) => setHourCurrency(e.target.value)}
                placeholder="Currency"
                className="w-[90px] border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-1 bg-transparent"
              />
              <input
                type="string"
                className="w-1/2 border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-1"
                placeholder="Hourly Rate (e.g. 50, 50-60)"
                value={hourRate}
                onChange={(e) => setHourRate(e.target.value)}
              />
              <span className="text-sm text-gray-500">per hour</span>
            </div>

            {/* Daily */}
            <div className="flex items-center gap-2">
              <input
                list="currency-options"
                value={dayCurrency}
                onChange={(e) => setDayCurrency(e.target.value)}
                placeholder="Currency"
                className="w-[90px] border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-1 bg-transparent"
              />
              <input
                type="string"
                className="w-1/2 border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-1"
                placeholder="Daily Rate (e.g. 500, 500-600)"
                value={dayRate}
                onChange={(e) => setDayRate(e.target.value)}
              />
              <span className="text-sm text-gray-500">per day</span>
            </div>

            <datalist id="currency-options">
              {currencies.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Glow animation */}
      <style>
        {`
          @keyframes glowBurst {
            0% {
              box-shadow: 0 0 0px 0px rgba(54, 85, 97, 0);
            }
            30% {
              box-shadow: 0 0 20px 6px rgba(54, 85, 97, 0.6);
            }
            100% {
              box-shadow: 0 0 15px 3px rgba(54, 85, 97, 0.6);
            }
          }

          .glow-burst {
            animation: glowBurst 0.8s ease-out forwards;
            border-radius: 0.75rem;
          }

          .glow-hold {
            box-shadow: 0 0 20px 6px rgba(54, 85, 97, 0.6);
            border-radius: 0.75rem;
          }
        `}
      </style>
    </div>
  );
}
